import { useState } from 'react';
import { IconLoader2 } from '@tabler/icons-react';
import { WarningMessage } from '@common/components';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import useTranslation from '@/hooks/useTranslation';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isDeleting?: boolean;
}

function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
}: DeleteAccountModalProps) {
  const { lang } = useTranslation();
  const [confirmationText, setConfirmationText] = useState('');

  const handleClose = () => {
    setConfirmationText('');
    onClose();
  };

  const isConfirmationValid =
    confirmationText.toLowerCase() === lang.get('msg.deleteMyAccount').toLowerCase();

  const handleConfirmDelete = async () => {
    if (isConfirmationValid) {
      await onConfirm();
      setConfirmationText('');
    }
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent className="w-full max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center">
            <span className="text-xl font-semibold">{lang.get('msg.deleteAccount')}</span>
          </AlertDialogTitle>
        </AlertDialogHeader>

        <div className="mb-6 text-dark-text text-sm">
          <p className="text-sm mb-1 text-dark-text">
            {lang.get('msg.areYouSureYouWantToDeleteYourAccount')}
          </p>
          <p>
            {lang.get('msg.thisActionIs')}{' '}
            <span className="font-semibold">{lang.get('msg.irreversible')}</span>
            {lang.get('msg.itWill')}
          </p>
          <ul className="list-disc list-inside pl-1 text-sm pt-4 pb-3 text-dark-text">
            <li>{lang.get('msg.removePersonalData')}</li>
            <li>{lang.get('msg.removeFromOrganizations')}</li>
            <li>{lang.get('msg.deleteOwnedOrganizations')}</li>
            <li>{lang.get('msg.deleteSoleOwnershipEntities')}</li>
          </ul>
          <WarningMessage>
            <p>{lang.get('msg.transferOwnershipSuggestion')}</p>
          </WarningMessage>
          <p className="pt-4">
            {lang.get('msg.typeExactlyAsAppears')}{' '}
            <span className="font-semibold italic">{lang.get('msg.deleteMyAccount')}</span>{' '}
            {lang.get('msg.exactlyAsItAppears')}
          </p>
          <Input
            type="text"
            placeholder={lang.get('msg.deleteMyAccount')}
            value={confirmationText}
            onChange={({ target }) => setConfirmationText(target.value)}
            className="mt-3"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose}>{lang.get('msg.cancel')}</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirmDelete}
            disabled={!isConfirmationValid || isDeleting}
          >
            {isDeleting ? (
              <>
                <IconLoader2 className="w-4 h-4 mr-2 animate-spin" />
                {lang.get('msg.deleting')}
              </>
            ) : (
              lang.get('msg.deleteAccountPermanently')
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DeleteAccountModal;
